/**
 * Pacing rules from the prototype. The useful mid-quarter question isn't "over or under budget"
 * but "faster or slower than the clock": compare % of quarter BUDGET used with % of the QUARTER
 * elapsed.
 */
import type { Runway, Txn } from '@/api/types'

import { dayOffset, type QuarterCalendar } from './calendar'

export type PaceStatus = 'good' | 'bad' | 'neutral'

export function pacePercent(runway: Pick<Runway, 'days_elapsed_in_quarter' | 'days_total_in_quarter'>): number {
  if (runway.days_total_in_quarter <= 0) return 0
  return (runway.days_elapsed_in_quarter / runway.days_total_in_quarter) * 100
}

export function pctOfBudget(actual: number, budget: number): number | null {
  return budget > 0 ? (actual / budget) * 100 : null
}

export function paceStatus(pctUsed: number | null, pacePct: number): PaceStatus {
  if (pctUsed === null) return 'neutral'
  return pctUsed <= pacePct ? 'good' : 'bad'
}

/**
 * Pace wording. "Over pace" = more of the budget used than of the quarter elapsed (spending faster
 * than the clock: bad); "under pace" = less (good). Never "ahead"/"behind", which read both ways.
 */
export function paceWords(pctUsed: number, pacePct: number): string {
  const diff = Math.round(pctUsed - pacePct)
  if (diff === 0) return 'on pace'
  return diff > 0 ? `${diff}pt over pace` : `${-diff}pt under pace`
}

/** Straight-line run-rate projection of quarter-to-date spend to the full quarter. */
export function projectedTotal(qtdActual: number, daysElapsed: number, daysTotal: number): number {
  if (daysElapsed <= 0) return qtdActual
  return qtdActual * (daysTotal / daysElapsed)
}

/** Variance direction for spend: at or under budget is good. */
export function varianceStatus(variance: number): 'good' | 'bad' {
  return variance <= 0 ? 'good' : 'bad'
}

/**
 * A finished period: the as-of date reached the period's last day (a past period, or the current
 * one on its last day). Wording switches from pace/projection to final over/under.
 */
export function isPeriodComplete(runway: Pick<Runway, 'period'>): boolean {
  return runway.period.complete
}

/**
 * The pace chart and the projection need one continuous time axis still running: adjacent months
 * whose as-of date is before their end (the original dashboard's rule).
 */
export function isPaceApplicable(runway: Pick<Runway, 'period'>): boolean {
  return runway.period.pace_applicable
}

/** Over/under wording of a variance (actual − budget): never "over" when under, "on budget" at 0. */
export function varianceWords(variance: number): 'over' | 'under' | 'on budget' {
  const cents = Math.round(variance * 100)
  if (cents === 0) return 'on budget'
  return cents > 0 ? 'over' : 'under'
}

/**
 * Cumulative spend by day: `series[k]` is the total of the first `k` days of the quarter
 * (`series[0] === 0`), for `k` up to `daysElapsed`.
 */
export function cumulativeSeries(txns: readonly Txn[], calendar: QuarterCalendar, daysElapsed: number): number[] {
  const elapsed = Math.max(0, Math.min(daysElapsed, calendar.totalDays))
  // In cents, so the last point equals the QTD actual exactly.
  const daily = new Array<number>(elapsed).fill(0)
  for (const txn of txns) {
    const offset = dayOffset(calendar, txn.date)
    if (offset === null || elapsed === 0) continue
    // A line the API counted but dated after today (an older API counted the whole as-of month)
    // lands on the last elapsed day, so the chart never disagrees with the QTD total.
    const day = Math.min(offset, elapsed - 1)
    daily[day] = (daily[day] ?? 0) + Math.round(txn.amt * 100)
  }
  const series = new Array<number>(elapsed + 1).fill(0)
  for (let k = 1; k <= elapsed; k += 1) series[k] = (series[k - 1] ?? 0) + (daily[k - 1] ?? 0)
  return series.map((cents) => cents / 100 || 0)
}
